import React, { FC } from 'react';
import { ForgeIcon, ForgeInlineMessage } from '@tylertech/forge-react';
import { I18n } from 'common/visualizations';
import { ScheduleSendValues } from '../../types/scheduleSend';
import { buildTranslationParams, getFormattedDates } from './helpers';
import { isValidScheduledReport } from '../../validators';

const scope = 'shared.components.schedule_send_report.modal.summary_section';

export interface ScheduleSendSummaryProps {
  scheduleSendValues: ScheduleSendValues;
}

const SummarySection: FC<ScheduleSendSummaryProps> = (props) => {
  const { scheduleSendValues } = props;
  const { frequencyConfig } = scheduleSendValues;
  const { type } = frequencyConfig.intervalInfo;

  const buildReportInfo = () => {
    const { endDate, hasEndDate } = frequencyConfig.datesInfo;
    const translationParams = buildTranslationParams(scheduleSendValues);
    const { everyString, days, time, startDate, recipientCount, recipientString, week } = translationParams;
    const { endDateString, endDateValue } = getFormattedDates(undefined, endDate);

    // To avoid security risks, we will not use dangerouslySetInnerHTML here since the values come from the user.
    let reportInfo = '';
    reportInfo = [
      scheduleSendValues.name,
      I18n.t('is_scheduled_to', { scope }),
      recipientCount,
      recipientString
    ].join(' ');

    const startDateElement = (
      <>
        , {I18n.t('starting', { scope })} <b>{startDate}</b>
      </>
    );

    const endDateElement =
      hasEndDate && endDateValue.isValid() ? (
        <>
          {' '}
          {I18n.t('and_ending_on', { scope })} <b>{endDateString}</b>.
        </>
      ) : (
        <>.</>
      );

    const dateTimeElement = (
      <>
        {I18n.t('at', { scope })} <b>{time}</b>
        {startDateElement}
        {endDateElement}
      </>
    );

    if (type === 'week') {
      return (
        <span>
          {reportInfo} {I18n.t('every', { scope })} <b>{everyString}</b> {I18n.t('on_label', { scope })}{' '}
          <b>{days}</b> {dateTimeElement}
        </span>
      );
    } else if (type === 'day') {
      return (
        <span>
          {reportInfo} {I18n.t('every', { scope })} <b>{everyString}</b> {dateTimeElement}
        </span>
      );
    } else if (type === 'month') {
      return (
        <span>
          {reportInfo} {I18n.t('on_the', { scope })}{' '}
          <b>
            {week} {days}
          </b>{' '}
          {I18n.t('of_every', { scope })} <b>{everyString}</b> {dateTimeElement}
        </span>
      );
    }
    return null;
  };

  return isValidScheduledReport(scheduleSendValues) ? (
    <div className="schedule-summary-container" data-testid="schedule-summary-container">
      <ForgeInlineMessage theme={'info'}>
        <span className="forge-typography--heading1">
          <b>{I18n.t('shared.components.schedule_send_report.modal.summary_section.title')}</b>
        </span>
        <span className="forge-typography--body1 summary-heading" data-testid="summary-content">
          {buildReportInfo()}
        </span>
        <ForgeIcon slot="icon" name="calendar_text_outline"></ForgeIcon>
      </ForgeInlineMessage>
    </div>
  ) : null;
};

export default SummarySection;
